import React, { useState, useRef, useEffect, useId, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/** How close to the screen's edges a tooltip that keeps on screen comes, in px. */
const SCREEN_MARGIN = 8;

export interface TooltipProps {
  content: React.ReactNode | ((onClose: () => void) => React.ReactNode);
  children: React.ReactElement;
  delay?: number; // Delay in milliseconds before showing tooltip
  position?: 'top' | 'bottom' | 'left' | 'right' | 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  /** When true, popup allows pointer events (e.g. clickable buttons) */
  interactive?: boolean;
  /** 'hover' = show on mouse enter, 'contextmenu' = show on contextmenu */
  trigger?: 'hover' | 'contextmenu';
  /**
   * With the 'hover' trigger, the tooltip also opens when its trigger is clicked
   * or tapped and when it gets keyboard focus, so touch and keyboard users reach
   * it too: an info icon's text, for example. A click only opens or closes it:
   * its default action, such as submitting a form, is prevented. Once opened that
   * way it stays open until the trigger is clicked again, Escape is pressed,
   * focus leaves the trigger or anything else is pressed. While it shows, its
   * text describes the trigger (aria-describedby). The trigger must take
   * onClick, onFocus, onBlur and aria-describedby, as Button does.
   */
  openOnClickAndFocus?: boolean;
  /**
   * When true, the tooltip stays inside the screen: its lines wrap where they
   * are wider than the screen, and, before it is painted, it moves sideways as
   * far as it would otherwise pass the screen's left or right edge, its arrow
   * still at the trigger. An info icon's text, for example, near either edge of
   * a wide drawer or on a narrow screen.
   */
  keepOnScreen?: boolean;
  /** Callback when visibility changes */
  onVisibleChange?: (isVisible: boolean) => void;
}

/** Whether the element has the focus a keyboard gives, which browsers mark
 *  :focus-visible. A click's focus isn't marked, so it leaves the click to decide. */
const hasKeyboardFocus = (element: EventTarget | null): boolean => {
  try {
    return element instanceof Element && element.matches(':focus-visible');
  } catch {
    return false; // A browser without :focus-visible: the click still opens it.
  }
};

export const Tooltip: React.FC<TooltipProps> = ({ 
  content, 
  children, 
  delay = 200, // Default 200ms delay (much faster than browser default ~1000ms)
  position = 'top',
  interactive = false,
  trigger = 'hover',
  openOnClickAndFocus = false,
  keepOnScreen = false,
  onVisibleChange,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  // maxWidth: the screen's width less its margins, for one that keeps on screen.
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0, maxWidth: 0 });
  // How far one that keeps on screen moves sideways to stay inside it, in px.
  const [shiftX, setShiftX] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const targetRef = useRef<HTMLElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const tooltipId = useId();
  const opensOnClickAndFocus = openOnClickAndFocus && trigger === 'hover';
  // Opened by a click or keyboard focus (openOnClickAndFocus): the pointer
  // leaving the trigger doesn't close it.
  const pinnedRef = useRef(false);

  const showTooltip = (target: HTMLElement, immediately = trigger === 'contextmenu') => {
    targetRef.current = target;
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const show = () => {
      const rect = target.getBoundingClientRect();
      let top = 0;
      let left = 0;

      switch (position) {
        case 'top':
          top = rect.top - 8;
          left = rect.left + rect.width / 2;
          break;
        case 'top-right':
          top = rect.top - 8;
          left = rect.right;
          break;
        case 'top-left':
          top = rect.top - 8;
          left = rect.left;
          break;
        case 'bottom':
          top = rect.bottom + 8;
          left = rect.left + rect.width / 2;
          break;
        case 'bottom-right':
          top = rect.bottom + 8;
          left = rect.right;
          break;
        case 'bottom-left':
          top = rect.bottom + 8;
          left = rect.left;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - 8;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + 8;
          break;
      }

      // Back where it opens, then within the screen's width without a scrollbar.
      setShiftX(0);
      setTooltipPosition({ top, left, maxWidth: document.documentElement.clientWidth - 2 * SCREEN_MARGIN });
      setIsVisible(true);
      onVisibleChange?.(true);
    };

    if (immediately) {
      show();
    } else {
      timeoutRef.current = setTimeout(show, delay);
    }
  };

  // Opens at once and stays open (openOnClickAndFocus).
  const pinOpen = (target: HTMLElement) => {
    pinnedRef.current = true;
    showTooltip(target, true);
  };

  const hideTooltip = () => {
    // Clear the timeout if the mouse leaves before the delay completes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    pinnedRef.current = false;
    setIsVisible(false);
    onVisibleChange?.(false);
  };

  const scheduleHide = () => {
    if (pinnedRef.current) return;
    if (!interactive) {
      hideTooltip();
      return;
    }
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(hideTooltip, 120);
  };

  const cancelScheduleHide = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // One that keeps on screen moves sideways before it is painted, as far as it
  // would otherwise pass the screen's left or right edge: its left edge wins
  // where it is as wide as the screen allows. Measured once each time it opens,
  // where it opens, since opening puts it back there (show).
  useLayoutEffect(() => {
    if (!keepOnScreen || !isVisible || !popupRef.current) return;
    const { left, right } = popupRef.current.getBoundingClientRect();
    const rightmost = document.documentElement.clientWidth - SCREEN_MARGIN;
    setShiftX(left < SCREEN_MARGIN ? SCREEN_MARGIN - left : right > rightmost ? rightmost - right : 0);
  }, [keepOnScreen, isVisible, tooltipPosition]);

  // A hover tooltip is dismissed by any pointer press, not only by the pointer
  // leaving the trigger.
  //
  // `mouseleave` is not enough: clicking a button that opens a modal or a
  // confirmation dialog puts a new element over the trigger without the pointer
  // moving, and the browser does not re-evaluate hover until it does — so no
  // `mouseleave` ever arrives and the tooltip stays up, floating above the
  // dialog on `z-[9999]`. The same happens when a click disables the trigger,
  // since a disabled control stops delivering mouse events.
  //
  // Capture phase, so it still fires when a handler underneath calls
  // stopPropagation.
  useEffect(() => {
    if (trigger !== 'hover' || !isVisible) return;
    const onPointerDown = (e: PointerEvent) => {
      // An interactive tooltip's own content is clickable — pressing inside it
      // is use, not dismissal.
      if (interactive && popupRef.current?.contains(e.target as Node)) return;
      // A press on the trigger of one that opens on click is the click's to
      // handle: it closes the tooltip or keeps it open.
      if (opensOnClickAndFocus && targetRef.current?.contains(e.target as Node)) return;
      hideTooltip();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [trigger, isVisible, interactive, opensOnClickAndFocus]);

  // Escape closes a tooltip that opens on click and focus, wherever focus is,
  // before anything around it hears the key (a dialog that closes on Escape
  // closes on the next one).
  useEffect(() => {
    if (!opensOnClickAndFocus || !isVisible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      hideTooltip();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [opensOnClickAndFocus, isVisible]);

  // When trigger is click, close on outside mousedown
  useEffect(() => {
    if (trigger !== 'contextmenu' || !isVisible) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (targetRef.current?.contains(target) || popupRef.current?.contains(target)) return;
      hideTooltip();
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [trigger, isVisible]);

  // Clone the child element and add event handlers based on trigger
  const childWithHandlers = React.cloneElement(children, {
    onMouseEnter: trigger === 'hover' ? (e: React.MouseEvent) => {
      showTooltip(e.currentTarget as HTMLElement);
      if (children.props.onMouseEnter) children.props.onMouseEnter(e);
    } : undefined,
    onMouseLeave: trigger === 'hover' ? (e: React.MouseEvent) => {
      scheduleHide();
      if (children.props.onMouseLeave) children.props.onMouseLeave(e);
    } : undefined,
    // Also on the trigger itself, not only via the document listener above:
    // that one is only armed once the tooltip is visible, so a click landing
    // inside the show delay would leave the timer running and pop the tooltip
    // up *after* the dialog had opened. `hideTooltip` clears it. A tooltip
    // that opens on click leaves the press to the click.
    onPointerDown: trigger === 'hover' && !opensOnClickAndFocus ? (e: React.PointerEvent) => {
      hideTooltip();
      if (children.props.onPointerDown) children.props.onPointerDown(e);
    } : children.props.onPointerDown,
    onContextMenu: trigger === 'contextmenu' ? (e: React.MouseEvent) => {
      e.preventDefault();
      if (isVisible) {
        hideTooltip();
      } else {
        showTooltip(e.currentTarget as HTMLElement);
      }
      if (children.props.onContextMenu) children.props.onContextMenu(e);
    } : children.props.onContextMenu,
    onClick: trigger === 'contextmenu' ? (e: React.MouseEvent) => {
      e.preventDefault();
      if (isVisible) {
        hideTooltip();
      } else {
        showTooltip(e.currentTarget as HTMLElement);
      }
      if (children.props.onClick) children.props.onClick(e);
    } : opensOnClickAndFocus ? (e: React.MouseEvent) => {
      // The click only opens or closes the tooltip: it opens it, or keeps open
      // one the pointer opened, and closes one a click or keyboard focus opened.
      e.preventDefault();
      if (isVisible && pinnedRef.current) {
        hideTooltip();
      } else {
        pinOpen(e.currentTarget as HTMLElement);
      }
      if (children.props.onClick) children.props.onClick(e);
    } : children.props.onClick,
    ...(opensOnClickAndFocus ? {
      onFocus: (e: React.FocusEvent) => {
        if (hasKeyboardFocus(e.currentTarget)) pinOpen(e.currentTarget as HTMLElement);
        if (children.props.onFocus) children.props.onFocus(e);
      },
      onBlur: (e: React.FocusEvent) => {
        // Focus moving into an interactive tooltip keeps it open.
        if (!(interactive && popupRef.current?.contains(e.relatedTarget as Node))) hideTooltip();
        if (children.props.onBlur) children.props.onBlur(e);
      },
      'aria-describedby': isVisible ? tooltipId : children.props['aria-describedby'],
    } : {}),
  });

  // Where the arrow sits along the tooltip's top or bottom edge.
  const arrowLeft = position === 'top' || position === 'bottom'
    ? '50%'
    : position === 'top-right' || position === 'bottom-right'
    ? 'calc(100% - 12px)'
    : '12px';

  // A tooltip that opens on click and focus is its trigger's description, and
  // takes its own presses, which close it, rather than letting them through to
  // what lies under it.
  return (
    <>
      {childWithHandlers}
      {isVisible && content != null && createPortal(
        <div
          ref={popupRef}
          {...(opensOnClickAndFocus ? { id: tooltipId, role: 'tooltip' } : {})}
          className={`fixed px-bg-v2-lg py-bg-v2-md bg-bg-primary text-fg-primary text-xs rounded-bg-v2-md shadow-lg border border-border-primary ${keepOnScreen ? 'w-max whitespace-normal' : 'whitespace-nowrap'} z-[9999] ${interactive || trigger === 'contextmenu' || opensOnClickAndFocus ? 'pointer-events-auto' : 'pointer-events-none'}`}
          style={{
            top: position === 'top' || position === 'top-right' || position === 'top-left'
              ? `${tooltipPosition.top}px`
              : position === 'bottom' || position === 'bottom-right' || position === 'bottom-left'
              ? `${tooltipPosition.top}px`
              : `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left + shiftX}px`,
            maxWidth: keepOnScreen ? `${tooltipPosition.maxWidth}px` : undefined,
            transform: position === 'top'
              ? 'translate(-50%, -100%)'
              : position === 'top-right'
              ? 'translate(-100%, -100%)'
              : position === 'top-left'
              ? 'translate(0, -100%)'
              : position === 'bottom'
              ? 'translate(-50%, 0)'
              : position === 'bottom-right'
              ? 'translate(-100%, 0)'
              : position === 'bottom-left'
              ? 'translate(0, 0)'
              : position === 'left'
              ? 'translate(-100%, -50%)'
              : 'translate(0, -50%)',
          }}
          onMouseEnter={interactive && trigger === 'hover' ? cancelScheduleHide : undefined}
          onMouseLeave={interactive && trigger === 'hover' ? hideTooltip : undefined}
        >
          {typeof content === 'function' ? content(hideTooltip) : content}
          {/* Arrow */}
          {(position === 'top' || position === 'bottom' || position === 'top-right' || position === 'top-left' || position === 'bottom-right' || position === 'bottom-left') && (
            <div
              className="absolute w-2 h-2 bg-bg-primary transform rotate-45"
              style={{
                [position === 'top' || position === 'top-right' || position === 'top-left' ? 'bottom' : 'top']: '-4px',
                // Moved back as far as the tooltip moved, so it stays at the trigger.
                left: shiftX ? `calc(${arrowLeft} - ${shiftX}px)` : arrowLeft,
                transform: (position === 'top' || position === 'bottom') 
                  ? 'translateX(-50%) rotate(45deg)' 
                  : 'rotate(45deg)',
              }}
            />
          )}
          {(position === 'left' || position === 'right') && (
            <div
              className="absolute w-2 h-2 bg-bg-primary transform rotate-45"
              style={{
                [position === 'left' ? 'right' : 'left']: '-4px',
                top: '50%',
                transform: 'translateY(-50%) rotate(45deg)',
              }}
            />
          )}
        </div>,
        document.body
      )}
    </>
  );
};

